import { zodResolver } from "@hookform/resolvers/zod";
import type { MutableRefObject, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { DeepMap, FieldError } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import styled, { useTheme } from "styled-components/macro";
import { z } from "zod";
import {
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../../../components/Buttons/Buttons";
import { SlideOut } from "../../../../../../components/SlideOut/SlideOut";
import { useNotifications } from "../../../../../../components/Notifications/NotificationsContext";
import {
  InfoIcon,
  NonVisibleIcon,
  NotApplicableIcon,
} from "../../../../../../components/Icons/Icons";
import type { OptionType, UUID } from "../../../../../../types/types";
import type {
  AttributeSchema,
  CollectionSchema,
} from "../../../../../../types/types.PIM";
import type { ApprovalConfigurationUIRules } from "../../../../../../types/types.Workflow";
import {
  createFormFieldForCollection,
  getValidationForProductCollection,
} from "./ProductCollectionSchema.util";
import { MultiSelectRefType } from "./type";
import type { FormValue, IRow, OnSubmitValue } from "./type";
import { useDependentAttributes } from "../../../../../../util/hooks";
import { getSelectionEntries } from "../../../../../SharedPages/OrganizationPage/ProductsList/ProductsList.util";
import { transformAddress } from "../../SellerAdminPIMAttributes/AddressAttributes/address.utils";
import { isValidPhoneNumber } from "../../../../../../util/util";
import { strings } from "../../../../../../util/strings";
import type { TFunction } from "i18next";
import { zodRequiredString } from "../../../../../../util/zod.util";
import { getEditBlockedReasonMessage } from "../../../../../../util/attributeEditability";

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_3xl")};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  div[class*="auto-filled-indicator"] {
    > svg {
      width: 20px;
      height: 20px;
    }
  }
  div[class*="multi-select-cell-wrapper"] {
    width: fit-content;
  }
`;

const FormFieldWithIcons = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const FormField = styled.div`
  flex: 1;
  min-width: 0;
`;

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  padding-top: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const CenteredIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  padding-top: ${({ theme }) => theme.resolve("spacing_md")};

  button {
    flex: 1;
  }
`;

export const FieldWithIcons = ({
  children,
  description,
  restricted,
  notApplicable,
  disabledTooltip,
  fieldId,
}: {
  children: ReactNode;
  description?: string | null;
  restricted?: boolean;
  notApplicable?: boolean;
  disabledTooltip?: string;
  fieldId: string;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const hasIcons = description || restricted || notApplicable;

  if (!hasIcons && !disabledTooltip) {
    return <>{children}</>;
  }

  return (
    <FormFieldWithIcons>
      <FormField>
        {disabledTooltip ? (
          <>
            <div
              data-tip={disabledTooltip}
              data-for={`disabled-tip-${fieldId}`}
            >
              {children}
            </div>
            <ReactTooltip id={`disabled-tip-${fieldId}`} effect="solid" />
          </>
        ) : (
          children
        )}
      </FormField>
      <IconsContainer>
        {description && (
          <>
            <CenteredIconContainer
              data-tip={description}
              data-for={`info-tip-${fieldId}`}
            >
              <InfoIcon fill={theme.secondaryIconColor} />
            </CenteredIconContainer>
            <ReactTooltip
              delayHide={500}
              id={`info-tip-${fieldId}`}
              clickable
              effect="solid"
            />
          </>
        )}
        {restricted && (
          <>
            <CenteredIconContainer
              data-for={`restricted-tip-${fieldId}`}
              data-tip={t("This attribute is only visible for internal users")}
            >
              <NonVisibleIcon
                width={24}
                height={24}
                fill={theme.secondaryIconColor}
              />
            </CenteredIconContainer>
            <ReactTooltip
              delayHide={500}
              id={`restricted-tip-${fieldId}`}
              effect="solid"
            />
          </>
        )}
        {notApplicable && (
          <>
            <CenteredIconContainer
              data-tip={t("This attribute is marked as not applicable")}
              data-for={`not-applicable-tip-${fieldId}`}
            >
              <NotApplicableIcon
                width={24}
                height={24}
                fill={theme.errorColor}
              />
            </CenteredIconContainer>
            <ReactTooltip
              delayHide={500}
              id={`not-applicable-tip-${fieldId}`}
              effect="solid"
            />
          </>
        )}
      </IconsContainer>
    </FormFieldWithIcons>
  );
};

export type CollectionSchemaSlideOutProps = {
  show: boolean;
  onClose: () => void;
  onSubmit: (value: OnSubmitValue) => Promise<void>;
  columns: AttributeSchema[];
  row: IRow | null;
  rowUuid: UUID | null;
  isNewRow: boolean;
  isClone: boolean;
  collectionSchema: CollectionSchema;
  tenant_id?: string;
  storefront_id?: string;
  isSubmitting: boolean;
  enable_reference_entities?: boolean;
  has_reference_entity_mapping?: boolean;
  enable_generic_sku?: boolean;
  generic_sku_packaging_type_name?: string;
  packagingTypeColId?: string;
  skuSourceColId?: string;
  allRows: IRow[];
  multiSelectRefs: MutableRefObject<{ [key: string]: MultiSelectRefType }>;
  excludeProfileId?: string;
  uiRules?: ApprovalConfigurationUIRules[];
};
export const addressSchema = (t: TFunction) =>
  z
    .object({
      country: z.object(
        { label: z.string(), value: z.string() },
        {
          required_error: t("This is a required field"),
          invalid_type_error: t("This is a required field"),
        }
      ),
      poc_country_code: z
        .object({
          label: z.string(),
          value: z.string(),
        })
        .nullable()
        .optional(),
      state: z.object(
        {
          label: z.string(),
          value: z.string(),
        },
        {
          required_error: t("This is a required field"),
          invalid_type_error: t("This is a required field"),
        }
      ),
      address1: zodRequiredString(t),
      address2: z.string().nullable().optional(),
      address3: z.string().nullable().optional(),
      city: zodRequiredString(t),
      postal_code: zodRequiredString(t),
      company_name: z.string().nullable().optional(),
      tax_id: z.string().nullable().optional(),
      crm_id: z.string().nullable().optional(),
      location_id: z.string().nullable().optional(),
      poc_user_id: z.string().uuid().nullable().optional(),
      poc_first_name: z.string().nullable().optional(),
      poc_last_name: z.string().nullable().optional(),
      poc_email: z.preprocess(
        (val) => (val === "" ? undefined : val),
        z.string().email().nullable().optional()
      ),
      poc_phone: z.string().nullable().optional(),
      country_name: z.string().optional(),
      state_display_name: z.string().optional(),
    })
    .refine(
      ({ poc_phone, poc_country_code }) => {
        if (!poc_phone) return true;
        return !!poc_country_code;
      },
      { message: strings(t).thisIsARequiredField, path: ["poc_country_code"] }
    )
    .refine(
      ({ poc_phone, poc_country_code }) => {
        if (!poc_phone || !poc_country_code) return true;
        return isValidPhoneNumber({
          phone_number: poc_phone,
          country_code: poc_country_code as OptionType<string>,
        });
      },
      { message: strings(t).phoneNumberMustBeValid, path: ["poc_phone"] }
    );

export const CollectionSchemaSlideOut = ({
  show,
  onClose,
  onSubmit,
  columns,
  row,
  rowUuid,
  isNewRow,
  isClone,
  collectionSchema,
  tenant_id,
  storefront_id,
  isSubmitting,
  enable_reference_entities,
  has_reference_entity_mapping,
  enable_generic_sku,
  generic_sku_packaging_type_name,
  packagingTypeColId,
  skuSourceColId,
  allRows,
  multiSelectRefs,
  excludeProfileId,
  uiRules,
}: CollectionSchemaSlideOutProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { t } = useTranslation();
  const { notifyError } = useNotifications();
  const errorRef = useRef<DeepMap<FormValue, FieldError> | null>(null);

  const getValidationSchema = useCallback(
    (currentPackagingType?: string) => {
      let validationSchemaObj: {
        [prop: string]:
          | z.ZodString
          | z.ZodBoolean
          | z.ZodSchema
          | z.ZodOptional<z.ZodString | z.ZodBoolean | z.ZodSchema>;
      } = {};

      const isBulkPackaging =
        enable_generic_sku &&
        currentPackagingType === generic_sku_packaging_type_name;

      columns?.forEach((col) => {
        const isPackagingField =
          (!!enable_generic_sku || isBulkPackaging) &&
          (col.object_type === "packaging_volume" ||
            col.object_type === "packaging_unit");

        let schema;
        if (col.input_type === "address") {
          schema = col.is_not_applicable
            ? z.any()
            : z.preprocess(
                (val) => {
                  if (!val || typeof val !== "object") return undefined;
                  const isEmpty = Object.values(val as object).every(
                    (v) => v === null || v === undefined || v === ""
                  );
                  return isEmpty ? undefined : val;
                },

                addressSchema(t).optional()
              );
        } else {
          schema = getValidationForProductCollection({
            input_type: col.input_type,
            is_multiple_choice: col.is_multiple_choice ?? false,
            optional: !col.is_required || !!isPackagingField,
            t,
          });
        }
        validationSchemaObj[col.id] = schema;
      });

      return z.object(validationSchemaObj).superRefine((formValue, ctx) => {
        const packagingTypeValue = packagingTypeColId
          ? (formValue[packagingTypeColId] as OptionType<string> | undefined)
          : undefined;

        const isBulkPackagingCurrent =
          enable_generic_sku &&
          (packagingTypeValue?.value === generic_sku_packaging_type_name ||
            currentPackagingType === generic_sku_packaging_type_name);

        if (!isBulkPackagingCurrent) {
          columns?.forEach((col) => {
            if (col.object_type === "packaging_volume" && col.is_required) {
              const volumeValue = formValue[col.id];
              if (
                !volumeValue ||
                (typeof volumeValue === "string" && volumeValue === "")
              ) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: t("Packaging volume is required"),
                  path: [col.id],
                });
              } else {
                const numericValue = Number(volumeValue);
                if (isNaN(numericValue) || numericValue <= 0) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t("must be a positive number"),
                    path: [col.id],
                  });
                }
              }
            }

            if (col.object_type === "packaging_unit" && col.is_required) {
              const unitValue = formValue[col.id] as
                | OptionType<any>
                | undefined;
              if (!unitValue || !unitValue.value) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: t("Packaging unit is required"),
                  path: [col.id],
                });
              }
            }
          });
        }

        if (
          Object.values(formValue).every(
            (
              value:
                | string
                | boolean
                | string[]
                | OptionType<any>
                | Record<string, unknown>
                | undefined
            ) => {
              if (typeof value === "string") {
                return value === "";
              }
              if (Array.isArray(value)) {
                return value.length === 0;
              }
              if (typeof value === "object") {
                return !value || Object.keys(value).length === 0;
              }
              return value === undefined || value === null;
            }
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("Select at least one field"),
            path: ["select_a_field"],
          });
        }
      });
    },
    [
      columns,
      enable_generic_sku,
      generic_sku_packaging_type_name,
      packagingTypeColId,
      t,
    ]
  );

  const colWithDefaultValues = useCallback(
    (col: AttributeSchema) => {
      return {
        ...col,
        default_values:
          collectionSchema.value_rows
            ?.find((row_item) =>
              isClone
                ? row_item.row_id === row?.cloned_original_uuid
                : row_item.row_id === rowUuid
            )
            ?.values?.filter((value) => value.attribute_id === col.id) ?? null,
      };
    },
    [collectionSchema.value_rows, isClone, row?.cloned_original_uuid, rowUuid]
  );

  const methodForm = useForm<FormValue>({
    resolver: zodResolver(getValidationSchema()),
  });

  const { handleSubmit, reset, errors, getValues, setValue } = methodForm;

  const {
    clearableAttributes,
    dependentAttributesOptions: dependent_attributes_options,
    updateDependentAttributes,
    resetDependentAttributes,
  } = useDependentAttributes({
    mode: "single",
    storefrontId: storefront_id ?? "",
    enabled: !!enable_reference_entities && !!has_reference_entity_mapping,
    contextType: "collection",
    contextId: collectionSchema.id,
    collectionSchemaName: collectionSchema.name,
    getFormValues: getValues,
    setFormValue: setValue as (attributeId: string, value: unknown) => void,
    getCurrentSelections: (formValues, constraintList) => {
      // Create a Set for faster lookup of dependent attribute IDs
      const dependentAttrIds = new Set(
        constraintList.map((c) => c.attribute_id)
      );

      return Object.entries(formValues).flatMap(([key, value]) => {
        if (!dependentAttrIds.has(key) || !value) {
          return [];
        }

        // Memoize the columns lookup using a map for better performance if columns is large
        const col = columns.find((c) => c.id === key);
        if (
          col &&
          col.is_multiple_choice &&
          Array.isArray(value) &&
          colWithDefaultValues
        ) {
          const defaultValues = colWithDefaultValues(col).default_values;
          // Exclude selections that are present in defaultValues
          const filteredValues = (
            value as Array<string | { name: string }>
          ).filter(
            (v) =>
              !defaultValues?.some((dv) =>
                typeof v === "string"
                  ? dv.enum_variant?.variant === v
                  : dv.enum_variant?.variant === v.name
              )
          );
          return getSelectionEntries(key, filteredValues);
        }
        return getSelectionEntries(key, value);
      });
    },
    attributesOrColumns: columns ?? [],
    multiSelectRefs,
  });

  const closeSlideOut = useCallback(() => {
    resetDependentAttributes();
    setIsInitialized(false);
    onClose();
  }, [resetDependentAttributes, onClose]);

  const getDependentAttributesConstraint = useCallback(
    (col_id: string) => {
      return dependent_attributes_options.find(
        (dependent_attribute) => dependent_attribute.attribute_id === col_id
      );
    },
    [dependent_attributes_options]
  );

  const handleFormSubmit = handleSubmit(
    async (values: FormValue) => {
      const modifiedValues: FormValue = {};
      Object.entries(values).forEach(([key, value]) => {
        const col = columns.find((c) => c.id === key);

        if (col?.object_type === "sku_status" && skuSourceColId) {
          const skuSourceVal = values[skuSourceColId] as
            | { value?: string }
            | undefined;
          const isDisabled =
            !!skuSourceVal &&
            (skuSourceVal.value ?? "").toLowerCase().trim() === "emea" &&
            collectionSchema.object_type?.toLowerCase()?.trim() === "skus";
          if (isDisabled) return;
        }

        if (Array.isArray(value)) {
          modifiedValues[key] =
            value.length === 0
              ? ""
              : value.map((val: any) => {
                  if (val && typeof val === "object") {
                    if ("name" in val) return val.name;
                    if ("value" in val) return val.value;
                  }
                  return val;
                });
        } else if (col?.input_type === "address") {
          modifiedValues[key] =
            transformAddress(value as Record<string, any>) ?? undefined;
        } else if (value instanceof Date) {
          modifiedValues[key] = value.toISOString();
        } else {
          modifiedValues[key] = value;
        }
      });
      await onSubmit({
        ...modifiedValues,
        row_id: rowUuid!,
      });
      resetDependentAttributes();
      setIsInitialized(false);
    },
    (formErrors: DeepMap<FormValue, FieldError>) => {
      errorRef.current = formErrors;
      if (formErrors["select_a_field"]) {
        notifyError(t("Enter at least one field."));
      } else {
        const errorKeys = Object.keys(formErrors);
        if (errorKeys.length > 0) {
          console.error("Validation failed for fields:", errorKeys);
          const firstError = formErrors[errorKeys[0]];
          if (firstError?.message) {
            notifyError(firstError.message);
          }
        }
      }
    }
  );

  const getHeaderTitle = () => {
    if (isNewRow && !isClone) {
      return t("Add New {{name}}", { name: collectionSchema.name });
    }
    if (isClone) {
      return t("Clone {{name}}", { name: collectionSchema.name });
    }
    return t("Edit {{name}}", { name: collectionSchema.name });
  };

  // Create a mock row for form field generation
  const formRow: IRow = row ?? {
    uuid: rowUuid ?? "",
    edit: true,
    row_editable: true,
  };

  // Reset form when row changes or slideout opens
  useEffect(() => {
    if (!show) {
      reset();
      errorRef.current = null;
    }
  }, [show, rowUuid, reset]);

  useEffect(() => {
    if (
      show &&
      (!!row || isNewRow) &&
      enable_reference_entities &&
      has_reference_entity_mapping &&
      !isInitialized
    ) {
      if (isNewRow && !isClone) {
        updateDependentAttributes({});
      } else if (!!row) {
        const { edit, id, row_editable, uuid, oldUUID, ...rest } = row;
        updateDependentAttributes({
          clonedSelectionsValues: rest,
        });
      }
      setIsInitialized(true);
    }
  }, [
    enable_reference_entities,
    has_reference_entity_mapping,
    isClone,
    isInitialized,
    isNewRow,
    row,
    show,
    updateDependentAttributes,
  ]);

  return (
    <SlideOut show={show} closeFlyout={closeSlideOut} title={getHeaderTitle()}>
      <FormWrapper>
        <Form
          noValidate
          onSubmit={handleFormSubmit}
          id={rowUuid ?? "new-row-form"}
        >
          {columns
            .filter((col) => col.input_type !== "multiline_entry")
            .map((col) => {
              const value = row ? row[col.id] : null;

              return (
                <FieldWithIcons
                  key={col.id}
                  fieldId={col.id}
                  description={col.description ? t([col.description]) : null}
                  restricted={col.is_restricted}
                  notApplicable={col.is_not_applicable}
                  disabledTooltip={
                    getEditBlockedReasonMessage(col.edit_blocked_reason, t) ||
                    undefined
                  }
                >
                  {createFormFieldForCollection({
                    col: colWithDefaultValues(col),
                    value,
                    row: formRow,
                    methodsOfUseForm: methodForm,
                    errors: errorRef.current ?? errors ?? {},
                    tenant_id,
                    storefront_id,
                    t,
                    has_reference_entity_mapping:
                      !!enable_reference_entities &&
                      !!has_reference_entity_mapping &&
                      getDependentAttributesConstraint(col.id) !== undefined,
                    dependent_attributes_constraint:
                      getDependentAttributesConstraint(col.id),
                    genericConfig: {
                      isBulkPackaging: false,
                      packagingTypeColId,
                      genericSkuPackagingTypeName:
                        generic_sku_packaging_type_name,
                      sku_source_col_id: skuSourceColId,
                      columns: columns,
                      enable_generic_sku,
                      uiRules,
                    },
                    updateDependentAttributes,
                    allRows,
                    allColumns: columns,
                    collectionSchemaName: collectionSchema.name,
                    multiSelectRefs,
                    clearableAttributes,
                    all_dependent_constraints: dependent_attributes_options,
                    excludeProfileId,
                  })}
                </FieldWithIcons>
              );
            })}
        </Form>
        <ButtonContainer>
          <TertiaryButtonLargeV2 type="button" onClick={closeSlideOut}>
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2
            type="submit"
            form={rowUuid ?? "new-row-form"}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </FormWrapper>
    </SlideOut>
  );
};
